// Legal footer for public-facing pages.
// Two variants:
//   <LegalFooter />        — full multi-column footer (Landing, Directory, Jobs).
//   <LegalFooter compact/> — single-row link cluster (legal pages, marketing sub-pages).
//
// Every variant exposes the "Cookie preferences" trigger via `openManager()`
// so users can revisit their consent choices at any time.

import { Link } from "react-router-dom";
import BrandMark from "@/components/BrandMark";
import SocialLinks from "@/components/SocialLinks";
import { useConsent } from "./cookieConsent";

const LEGAL_LINKS = [
  { path: "/privacy",        label: "Privacy Policy" },
  { path: "/terms",          label: "Terms & Conditions" },
  { path: "/cookies",        label: "Cookie Policy" },
  { path: "/acceptable-use", label: "Acceptable Use" },
  { path: "/disclaimer",     label: "Disclaimer" },
];

const PRODUCT_LINKS = [
  { path: "/",           label: "Home" },
  { path: "/directory",  label: "Directory" },
  { path: "/jobs-board", label: "Jobs board" },
  { path: "/pricing",    label: "Pricing" },
];

const RESOURCES_LINKS = [
  { path: "/ai-policy",           label: "Responsible AI" },
  { path: "/subscription-policy", label: "Billing & subscription" },
  { path: "/refund-policy",       label: "Refunds" },
  { path: "/data-retention",      label: "Data retention" },
  { path: "/accessibility",       label: "Accessibility" },
  { path: "/security",            label: "Security" },
  { path: "/copyright",           label: "Copyright / DMCA" },
];

// --- compact variant ---------------------------------------------------------
export function LegalFooterCompact({ className = "" }) {
  const { openManager } = useConsent();
  return (
    <div
      className={`text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-2 items-center ${className}`}
      data-testid="legal-footer-compact"
    >
      <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
      <span aria-hidden>·</span>
      <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
      <span aria-hidden>·</span>
      <Link to="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
      <span aria-hidden>·</span>
      <button
        type="button"
        onClick={openManager}
        className="hover:text-foreground transition-colors underline-offset-2 hover:underline"
        data-testid="legal-footer-cookie-prefs"
      >
        Cookie preferences
      </button>
    </div>
  );
}

// --- full variant ------------------------------------------------------------
export default function LegalFooter() {
  const { openManager } = useConsent();
  const year = new Date().getFullYear();
  return (
    <footer
      className="border-t border-border bg-card/30 mt-24"
      data-testid="legal-footer"
      aria-label="Site footer"
    >
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2.5" data-testid="legal-footer-home">
              <BrandMark size={32} className="rounded-lg" />
              <span className="font-display font-bold tracking-tight text-lg">Career OS</span>
            </Link>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-xs">
              An AI career operating system — for the next 20 years, not the next job.
            </p>
            <SocialLinks className="mt-5 -ml-1.5" testId="footer-social-links" />
          </div>

          <FooterColumn title="Product" links={PRODUCT_LINKS} />
          <FooterColumn title="Legal" links={LEGAL_LINKS} extra={
            <li>
              <button
                type="button"
                onClick={openManager}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline"
                data-testid="legal-footer-cookie-prefs"
              >
                Cookie preferences
              </button>
            </li>
          } />
          <FooterColumn title="Resources" links={RESOURCES_LINKS} extra={
            <li>
              <a
                href="mailto:support@careeros-ai.com"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Support
              </a>
            </li>
          } />
        </div>

        <div className="mt-12 pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>© {year} CareerOS-AI · Ontario, Canada</div>
          <div className="font-mono uppercase tracking-widest">Educational content — not regulated advice</div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links, extra }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4">
        {title}
      </div>
      <ul className="space-y-2 list-none pl-0">
        {links.map((l) => (
          <li key={l.path}>
            <Link
              to={l.path}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </Link>
          </li>
        ))}
        {extra}
      </ul>
    </div>
  );
}
